import React from 'react';
import CheckCircleIcon from '../icons/CheckCircleIcon';

export interface SummaryItem {
  label: string;
  count: number;
  isHighlight?: boolean;
}

export interface SummaryAlertAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

export interface SummaryAlertProps {
  /**
   * Title of the alert
   */
  title: string;
  /**
   * Summary items to display
   */
  items: SummaryItem[];
  /**
   * Optional description text
   */
  description?: string;
  /**
   * Optional action buttons to display at the bottom
   */
  actions?: SummaryAlertAction[];
  /**
   * Callback when the alert is dismissed
   */
  onDismiss?: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * SummaryAlert component displays a success message with a breakdown of items
 * Re-uses existing styles and patterns from the application
 */
const SummaryAlert: React.FC<SummaryAlertProps> = ({
  title,
  items,
  description,
  actions,
  onDismiss,
  className = '',
}) => {
  return (
    <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`} role="alert">
      <div className="flex items-start gap-3">
        <CheckCircleIcon className="text-green-600 flex-shrink-0 mt-0.5" width={20} height={20} />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-green-800 mb-2">{title}</h3>
          {description && <p className="text-sm text-green-700 mb-3">{description}</p>}
          <div className="space-y-1 mb-3">
            {items.map((item, index) => (
              <div
                key={index}
                className={`flex justify-between text-sm ${
                  item.isHighlight ? 'font-semibold text-green-900' : 'text-green-700'
                }`}
              >
                <span>{item.label}:</span>
                <span>{item.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
          {actions && actions.length > 0 && (
            <div className="flex gap-2 mt-4">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    action.variant === 'primary'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-white text-green-700 border border-green-300 hover:bg-green-50'
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-green-600 hover:text-green-800 flex-shrink-0"
            aria-label="Dismiss"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default SummaryAlert;
